import React, { useCallback } from 'react';
import { Button, Typography, Paper, Box, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Swal from 'sweetalert2';
import { GroupedActivity } from './interfaces';

interface Step3FileUploadProps {
  selectedGroupData: GroupedActivity['groups'][0];
  selectedProject: string;
  startDate: string;
  endDate: string;
  cardFiles: File[];
  attachmentFiles: File[];
  onCardFilesChange: (files: File[]) => void;
  onAttachmentFilesChange: (files: File[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export const Step3FileUpload: React.FC<Step3FileUploadProps> = ({
  selectedGroupData,
  selectedProject,
  startDate,
  endDate,
  cardFiles,
  attachmentFiles,
  onCardFilesChange,
  onAttachmentFilesChange,
  onNext,
  onBack,
}) => {
  // Función común que separa los archivos automáticamente
  const handleFileDrop = useCallback((acceptedFiles: File[]) => {
    const txtFiles: File[] = [];
    const otherFiles: File[] = [];

    acceptedFiles.forEach(file => {
      if (file.name.toLowerCase().endsWith('.txt')) {
        txtFiles.push(file);
      } else {
        otherFiles.push(file);
      }
    });

    // Agregar archivos .txt a cartas dinámicas
    if (txtFiles.length > 0) {
      onCardFilesChange([...cardFiles, ...txtFiles]);
    }

    // Agregar otros archivos a adjuntos generales
    if (otherFiles.length > 0) {
      onAttachmentFilesChange([...attachmentFiles, ...otherFiles]);
    }
  }, [cardFiles, attachmentFiles, onCardFilesChange, onAttachmentFilesChange]);

  const { getRootProps: getRootPropsCards, getInputProps: getInputPropsCards, isDragActive: isDragActiveCards } = useDropzone({
    onDrop: handleFileDrop,
    multiple: true,
    noClick: false,
  });

  const { getRootProps: getRootPropsAttachments, getInputProps: getInputPropsAttachments, isDragActive: isDragActiveAttachments } = useDropzone({
    onDrop: handleFileDrop,
    multiple: true,
    noClick: false,
  });

  const removeCardFile = (index: number) => {
    const newFiles = cardFiles.filter((_, i) => i !== index);
    onCardFilesChange(newFiles);
  };

  const removeAttachmentFile = (index: number) => {
    const newFiles = attachmentFiles.filter((_, i) => i !== index);
    onAttachmentFilesChange(newFiles);
  };

  const handleContinue = () => {
    const requiresCards = selectedGroupData.activities.some(a => a.shouldHasCardsLoaded);
    
    if (requiresCards && cardFiles.length === 0) {
      Swal.fire({
        title: 'Error',
        text: 'Debe cargar al menos un archivo .txt para las actividades que requieren cartas',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    onNext();
  };

  return (
    <Grid container className="dyn-cards-wizard-container" spacing={2}>
      <Paper className="dyn-cards-wizard-paper" elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Typography className="dyn-cards-wizard-title" sx={{ fontSize: 16, fontWeight: 600, mb: 2, flexShrink: 0 }}>
          PASO 3: CARGAR ARCHIVOS
        </Typography>

        {/* Header con detalles del grupo */}
        <Box sx={{ 
          mb: 2, 
          p: { xs: 1, sm: 1.5 }, 
          bgcolor: 'grey.100', 
          borderRadius: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-start' },
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Proyecto:</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{selectedProject}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Grupo:</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{selectedGroupData.groupAssigned}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Fecha:</Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{startDate} - {endDate}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actividades:</Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{selectedGroupData.totalActivities}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Cartas Esperadas:</Typography>
            <Typography variant="body2" color="warning.main" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{selectedGroupData.shouldHaveCards}</Typography>
          </Box>
        </Box>

        {/* Contenedor con scroll para las zonas de carga */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          minHeight: 0, 
          mb: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}>
          <Grid container spacing={2}>
            {/* Zona de carga de cartas */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                mb: { xs: 3, md: 0 }
              }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Cartas Dinámicas (.txt)</Typography>
                <Box
                  {...getRootPropsCards()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActiveCards ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    textAlign: 'center',
                    bgcolor: isDragActiveCards ? 'action.hover' : 'grey.50',
                    cursor: 'pointer',
                    flex: '0 0 auto',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <input {...getInputPropsCards()} />
                  <CloudUploadIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isDragActiveCards ? 'Suelte los archivos aquí' : 'Arrastre archivos aquí o haga clic para seleccionar'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Los archivos .txt se agregarán automáticamente aquí
                  </Typography>
                </Box>
                {cardFiles.length > 0 && (
                  <Box sx={{ 
                    mt: 2, 
                    flex: 1, 
                    overflowY: 'auto',
                    maxHeight: '300px',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '3px',
                    },
                  }}>
                    <List>
                      {cardFiles.map((file, index) => (
                        <ListItem key={index} sx={{ px: 1 }}>
                          <ListItemText 
                            primary={file.name} 
                            secondary={`${(file.size / 1024).toFixed(2)} KB`}
                            primaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                            secondaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => removeCardFile(index)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Zona de carga de adjuntos */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column'
              }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Adjuntos Generales</Typography>
                <Box
                  {...getRootPropsAttachments()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActiveAttachments ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    textAlign: 'center',
                    bgcolor: isDragActiveAttachments ? 'action.hover' : 'grey.50',
                    cursor: 'pointer',
                    flex: '0 0 auto',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <input {...getInputPropsAttachments()} />
                  <CloudUploadIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isDragActiveAttachments ? 'Suelte los archivos aquí' : 'Arrastre archivos aquí o haga clic para seleccionar'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Los archivos (excepto .txt) se agregarán automáticamente aquí
                  </Typography>
                </Box>
                {attachmentFiles.length > 0 && (
                  <Box sx={{ 
                    mt: 2, 
                    flex: 1, 
                    overflowY: 'auto',
                    maxHeight: '300px',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '3px',
                    },
                  }}>
                    <List>
                      {attachmentFiles.map((file, index) => (
                        <ListItem key={index} sx={{ px: 1 }}>
                          <ListItemText 
                            primary={file.name} 
                            secondary={`${(file.size / 1024).toFixed(2)} KB`}
                            primaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                            secondaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => removeAttachmentFile(index)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} justifyContent="space-between" sx={{ mt: 'auto', flexShrink: 0 }}>
          <Button variant="outlined" onClick={onBack}>
            Atrás
          </Button>
          <Button variant="contained" onClick={handleContinue}>
            Continuar
          </Button>
        </Grid>
      </Paper>
    </Grid>
  );
};

